import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  AddAction,
  Button,
  EmptyStateProps,
  MobileSearchField,
  failedEmptyState,
  searchEmptyState,
  useIsMobile,
  useLoadPhase,
} from "../../../components/ui";
// Only for the first-run state below — see `EmptyState`'s `welcome` variant.
// The services mark rather than the plans one: same disc, drawn in purple, so
// the two neighbouring tabs do not open on the same picture.
import { ReactComponent as FirstServiceMark } from "../../../assets/svg/No data images/services.svg";
import { useServices } from "../hooks/useServices";
import type { BrandService, NewBrandService } from "../types/brand.types";
import ServiceDetailsModal from "./ServiceDetailsModal";
import ServiceFormModal from "./ServiceFormModal";
import ServicesGrid from "./ServicesGrid";

/**
 * The services a brand offers, listed as cards beside the Plans tab's table.
 *
 * Everything about where the list comes from is `useServices`' business: this
 * file reads the state it hands back and knows nothing about the two calls
 * behind it. What the tab does have to know is that a card's name may be a
 * goal's or the practitioner's own — and it does not, because by the time it
 * gets here both are just a name.
 */
const BrandServicesTab: React.FC = () => {
  const {
    services,
    status,
    reload,
    suggestions,
    saving,
    create,
    update,
    setActive,
  } = useServices();

  // A list that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");
  // Which shape the tab takes — the card with its header row, or the phone's
  // column with the search on a line of its own. See the render below.
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  /** The card whose details are open. `null` is the dialog shut. */
  const [selected, setSelected] = useState<BrandService | null>(null);
  /**
   * The form dialog is open when this is not `undefined`: `null` creates, a
   * service edits. One flag rather than two, so the dialog cannot be asked to
   * do both at once.
   */
  const [editing, setEditing] = useState<BrandService | null | undefined>(
    undefined
  );

  // Filtered here rather than at the server, for the reason the plans list is:
  // the whole list is in hand — the endpoint sends every active service, not a
  // page of them — and it is a dozen cards. The day a search endpoint exists,
  // this memo becomes the fetch.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return services;
    return services.filter(
      (service) =>
        service.name.toLowerCase().includes(query) ||
        service.description.toLowerCase().includes(query)
    );
  }, [services, search]);

  /** What the grid shows in place of cards, whichever way it came to have none. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed")
      return failedEmptyState(
        "We could not load your services",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );

    if (search.trim()) return searchEmptyState("services");

    // A brand that has not said what it does yet, which is where every
    // workspace starts: the first-run welcome rather than a list reporting a
    // result.
    return {
      variant: "welcome",
      illustration: <FirstServiceMark aria-hidden className="w-11 h-11" />,
      title: "Add the services you offer",
      description: "Your services will show up once you create them.",
      highlights: {
        title: "With services, you can",
        items: [
          "Clearly describe each service you provide.",
          "Help clients understand what to expect from your service.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setEditing(null)}
        >
          Create service
        </Button>
      ),
    };
  };

  /**
   * Creates or saves, and takes the card from what the call answers with
   * rather than from the draft — the id, the created date and the status are
   * the server's to set, and on a curated service so is the name.
   *
   * The dialog stays open on a failure, which the hook has already reported:
   * closing it would throw away a filled-in form over a request that can be
   * retried by pressing the same button again.
   */
  const handleSubmit = async (draft: NewBrandService) => {
    if (editing) {
      const saved = await update(editing, draft);
      if (!saved) return;

      // Back to the details it was opened from, now reading what was saved.
      // Anyone who edits twice in a row would otherwise have to find the card
      // again between attempts.
      setSelected(saved);
    } else {
      const saved = await create(draft);
      if (!saved) return;

      // A live search would hide the card just added.
      setSearch("");
    }

    setEditing(undefined);
  };

  /**
   * Confirmed inside the details dialog, so the listing is changed at the
   * server and the card rebuilt from what it answers with — the pill follows
   * the backend's `is_active` rather than the button that was pressed.
   *
   * The dialog stays open on the saved card rather than closing: the pill is
   * the answer to what was just asked, and a panel that vanished would leave
   * it to be found again in the grid.
   */
  const handleSetActive = async (service: BrandService, active: boolean) => {
    const saved = await setActive(service, active);
    if (!saved) return;
    setSelected(saved);
  };

  /** The heading, and the way to add a service. The two of them are a row of
   *  their own on a phone with the search under it, and share one row with the
   *  search from `sm` up — the same split the Plans tab makes. */
  const heading = (
    <h2
      className={
        isMobile
          ? "text-[16px] font-medium text-ink"
          : "text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]"
      }
    >
      {/* No count until the list is in: a "(0)" beside a heading that is
          still loading reads as an answer rather than as a blank. */}
      Total services{status === "ready" ? ` (${services.length})` : ""}
    </h2>
  );

  const addButton = (
    <AddAction
      label="Add service"
      onClick={() => setEditing(null)}
      className="flex-shrink-0"
    />
  );

  return (
    // One card holding the lot from `md` up. On a phone there is no card: the
    // services are cards of their own, and a border around a column of
    // bordered cards is a box inside a box that costs a phone's width twice
    // over. So the tab is only a column there and the cards sit on the page's
    // own background — the shape the Plans tab beside it already takes.
    <section
      className={`lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden ${
        isMobile
          ? "flex-1 min-h-0 overflow-hidden"
          : "rounded-md border border-line bg-white"
      }`}
    >
      {isMobile ? (
        <>
          <div className="flex-shrink-0 flex items-center justify-between gap-3 pb-3">
            {heading}
            {addButton}
          </div>

          {/* A line of its own at the full width of the screen — see
              `MobileSearchField`, which is the same field the plans and the
              roster search with. */}
          <div className="flex-shrink-0 pb-3">
            <MobileSearchField
              value={search}
              onChange={setSearch}
              placeholder="Search services"
            />
          </div>
        </>
      ) : (
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
          {heading}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search"
                aria-label="Search services"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>

            {addButton}
          </div>
        </div>
      )}

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it — the blocks it stands for are hidden from screen
          readers, being the shape of an answer rather than the answer. */}
      {status === "loading" && (
        <p role="status" className="sr-only">
          Loading your services…
        </p>
      )}

      <ServicesGrid
        services={filtered}
        phase={phase}
        empty={emptyState()}
        onSelect={setSelected}
        // No card around the grid on a phone, so no gutters inside one: the
        // cards go to the page's own edges, as the plans do.
        flush={isMobile}
      />

      <ServiceDetailsModal
        service={selected}
        saving={saving}
        onClose={() => setSelected(null)}
        // One dialog at a time: the details close as the form opens, rather
        // than the form opening on top of them.
        onEdit={(service) => {
          setSelected(null);
          setEditing(service);
        }}
        onSetActive={handleSetActive}
      />

      <ServiceFormModal
        open={editing !== undefined}
        service={editing}
        suggestions={suggestions}
        saving={saving}
        // One step back rather than out to the grid. An edit was opened from
        // the details, so leaving it without saving lands on the details
        // again — the same step back the listing confirmation inside that
        // dialog makes when it is cancelled. A create has nothing behind it:
        // it was opened from the header's button, and `editing` is `null`.
        onClose={() => {
          if (editing) setSelected(editing);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
    </section>
  );
};

export default BrandServicesTab;
